import { useState } from 'react';
import { useBalanceSheet, ReportAccountBalance } from '../../hooks/useReports';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { 
  Building2, 
  HandCoins, 
  PieChart, 
  Percent, 
  ChevronDown, 
  ChevronRight, 
  FileText, 
  FileSpreadsheet,
  Info
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';

interface BalanceSheetProps {
  asOfDate: string;
  onAsOfDateChange?: (date: string) => void;
}

const FINANCIAL_YEARS = [
  { label: 'FY 2026-2027', endDate: '2027-03-31' },
  { label: 'FY 2025-2026', endDate: '2026-03-31' },
  { label: 'FY 2024-2025', endDate: '2025-03-31' },
];

export function BalanceSheet({ asOfDate, onAsOfDateChange }: BalanceSheetProps) {
  const [selectedFY, setSelectedFY] = useState('FY 2026-2027');
  const [currentAsOfDate, setCurrentAsOfDate] = useState(asOfDate || '2027-03-31');
  const [isSummaryOpen, setIsSummaryOpen] = useState(true);
  const [isLiabilitiesOpen, setIsLiabilitiesOpen] = useState(true);
  const [isAssetsOpen, setIsAssetsOpen] = useState(true);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  const { data, isLoading, error } = useBalanceSheet(currentAsOfDate);

  const handleFYChange = (fyLabel: string) => {
    setSelectedFY(fyLabel);
    const fy = FINANCIAL_YEARS.find(f => f.label === fyLabel);
    if (fy) {
      setCurrentAsOfDate(fy.endDate);
      onAsOfDateChange?.(fy.endDate);
    }
  };

  const formatCurrency = (val: number) => {
    return `₹${(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const calcPercentage = (val: number, total: number) => {
    if (!total || total === 0) return '0.0%';
    const pct = (val / total) * 100;
    return `${pct.toFixed(1)}%`;
  };

  const handleDownloadCSV = () => {
    if (!data) return;
    const rows = [
      ['Balance Sheet', `As of ${currentAsOfDate}`],
      [''],
      ['LIABILITIES AND EQUITIES', 'Amount (INR)', '% of Total'],
      ...data.liabilities.map(l => [l.name, l.netBalance, calcPercentage(l.netBalance, data.totalLiabilitiesAndEquity)]),
      ...data.equity.map(e => [e.name, e.netBalance, calcPercentage(e.netBalance, data.totalLiabilitiesAndEquity)]),
      ['Current Year Net Profit', data.netProfit, calcPercentage(data.netProfit, data.totalLiabilitiesAndEquity)],
      ['Total Liabilities & Equities', data.totalLiabilitiesAndEquity, '100%'],
      [''],
      ['ASSETS', 'Amount (INR)', '% of Total'],
      ...data.assets.map(a => [a.name, a.netBalance, calcPercentage(a.netBalance, data.totalAssets)]),
      ['Total Assets', data.totalAssets, '100%'],
    ];

    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Balance_Sheet_${selectedFY.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowDownloadMenu(false);
  };

  if (isLoading) return <Skeleton className="h-[500px] w-full rounded-xl" />;
  if (error || !data) {
    return (
      <div className="p-8 text-center text-red-500 bg-red-50 dark:bg-red-950/20 rounded-xl border border-red-200">
        Failed to load Balance Sheet.
      </div>
    );
  }

  const totalCapital = data.totalEquity + data.netProfit;
  const debtToEquityRatio = totalCapital > 0 
    ? ((data.totalLiabilities / totalCapital) * 100).toFixed(0) + '%' 
    : '0%';

  return (
    <div className="space-y-6">
      {/* Top Filter Controls Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-2">
        <div className="flex flex-col">
          <label className="text-xs text-muted-foreground font-medium mb-1">Select Financial Year</label>
          <select
            value={selectedFY}
            onChange={(e) => handleFYChange(e.target.value)}
            className="border border-input rounded-md text-sm px-3 py-2 bg-background font-medium shadow-sm w-52"
          >
            {FINANCIAL_YEARS.map(fy => (
              <option key={fy.label} value={fy.label}>{fy.label}</option>
            ))}
          </select>
        </div>

        {/* Download As Dropdown */}
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDownloadMenu(!showDownloadMenu)}
            className="h-10 px-4 font-medium flex items-center gap-2 border-input shadow-sm"
          >
            Download As
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          </Button>

          {showDownloadMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-lg shadow-lg z-50 py-1 text-sm">
              <button
                onClick={handleDownloadCSV}
                className="w-full text-left px-4 py-2 hover:bg-muted flex items-center gap-2 text-foreground font-medium"
              >
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                CSV Spreadsheet
              </button>
              <button
                onClick={() => { window.print(); setShowDownloadMenu(false); }}
                className="w-full text-left px-4 py-2 hover:bg-muted flex items-center gap-2 text-foreground font-medium"
              >
                <FileText className="w-4 h-4 text-blue-600" />
                PDF / Print View
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Summary KPI Banner */}
      <div className="bg-slate-50/80 dark:bg-slate-900/50 rounded-xl border border-border/80 p-4 space-y-4">
        <button
          onClick={() => setIsSummaryOpen(!isSummaryOpen)}
          className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
        >
          {isSummaryOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span>Summary</span>
          <span className="text-xs text-muted-foreground font-normal">Figures are in INR (₹)</span>
        </button>

        {isSummaryOpen && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-1">
            {/* Total Assets Card */}
            <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border border-border/60 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 rounded-lg">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                  Total Assets <Info className="w-3 h-3 text-muted-foreground/60" />
                </div>
                <div className="text-base font-bold font-mono text-foreground mt-0.5">
                  {formatCurrency(data.totalAssets)}
                </div>
              </div>
            </div>

            {/* Total Liabilities Card */}
            <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border border-border/60 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-rose-50 dark:bg-rose-950/40 text-rose-600 rounded-lg">
                <HandCoins className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                  Total Liabilities <Info className="w-3 h-3 text-muted-foreground/60" />
                </div>
                <div className="text-base font-bold font-mono text-foreground mt-0.5">
                  {formatCurrency(data.totalLiabilities)}
                </div>
              </div>
            </div>

            {/* Total Capital Card */}
            <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border border-border/60 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 rounded-lg">
                <PieChart className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                  Total Capital <Info className="w-3 h-3 text-muted-foreground/60" />
                </div>
                <div className="text-base font-bold font-mono text-foreground mt-0.5">
                  {formatCurrency(totalCapital)}
                </div>
              </div>
            </div>

            {/* Debt to Equity Ratio Card */}
            <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border border-border/60 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-950/40 text-blue-600 rounded-lg">
                <Percent className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                  Debt to Equity Ratio <Info className="w-3 h-3 text-muted-foreground/60" />
                </div>
                <div className="text-base font-bold font-mono text-foreground mt-0.5">
                  {debtToEquityRatio}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Two-Column Balance Sheet Table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Left Column: Liabilities and Equities */}
        <div className="bg-white dark:bg-slate-950 rounded-xl border border-border overflow-hidden shadow-sm flex flex-col justify-between h-full">
          <div>
            <div className="grid grid-cols-12 bg-slate-50 dark:bg-slate-900 px-4 py-3 text-xs font-semibold text-muted-foreground border-b border-border">
              <div className="col-span-6">Accounts</div>
              <div className="col-span-3 text-right">Amount</div>
              <div className="col-span-3 text-right">% of Total</div>
            </div>

            {/* Group Header: 1. Liabilities and equities */}
            <div className="divide-y divide-border/60">
              <div className="px-4 py-3 bg-slate-100/50 dark:bg-slate-900/40 font-bold text-sm text-foreground flex items-center justify-between">
                <button
                  onClick={() => setIsLiabilitiesOpen(!isLiabilitiesOpen)}
                  className="flex items-center gap-2 hover:text-primary transition-colors"
                >
                  {isLiabilitiesOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <span>1. Liabilities and equities</span>
                </button>
                <span className="font-mono text-xs font-bold text-foreground">
                  {formatCurrency(data.totalLiabilitiesAndEquity)}
                </span>
              </div>

              {isLiabilitiesOpen && (
                <div className="px-4 py-2 space-y-4">
                  {/* Subgroup: 1. Liability */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs font-bold text-slate-800 dark:text-slate-200 py-1.5 border-b border-border/40">
                      <span>1. Liability</span>
                      <span className="font-mono">{formatCurrency(data.totalLiabilities)}</span>
                    </div>

                    {data.liabilities.length === 0 ? (
                      <div className="text-xs text-muted-foreground italic py-1 pl-4">No liability accounts recorded.</div>
                    ) : (
                      data.liabilities.map((acc: ReportAccountBalance) => (
                        <div key={acc.accountId} className="grid grid-cols-12 text-xs py-1.5 hover:bg-slate-50 dark:hover:bg-slate-900/50 rounded px-2">
                          <div className="col-span-6 flex items-center gap-2">
                            <span className="font-mono text-muted-foreground text-[11px]">{acc.code}</span>
                            <span className="text-foreground font-medium">{acc.name}</span>
                          </div>
                          <div className="col-span-3 text-right font-mono font-medium text-foreground">{formatCurrency(acc.netBalance)}</div>
                          <div className="col-span-3 text-right font-mono text-muted-foreground">{calcPercentage(acc.netBalance, data.totalLiabilitiesAndEquity)}</div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Subgroup: 2. Capital */}
                  <div className="space-y-1 pt-2">
                    <div className="flex justify-between text-xs font-bold text-slate-800 dark:text-slate-200 py-1.5 border-b border-border/40">
                      <span>2. Capital</span>
                      <span className="font-mono">{formatCurrency(totalCapital)}</span>
                    </div>

                    {data.equity.map((acc: ReportAccountBalance) => (
                      <div key={acc.accountId} className="grid grid-cols-12 text-xs py-1.5 hover:bg-slate-50 dark:hover:bg-slate-900/50 rounded px-2">
                        <div className="col-span-6 flex items-center gap-2">
                          <span className="font-mono text-muted-foreground text-[11px]">{acc.code}</span>
                          <span className="text-foreground font-medium">{acc.name}</span>
                        </div>
                        <div className="col-span-3 text-right font-mono font-medium text-foreground">{formatCurrency(acc.netBalance)}</div>
                        <div className="col-span-3 text-right font-mono text-muted-foreground">{calcPercentage(acc.netBalance, data.totalLiabilitiesAndEquity)}</div>
                      </div>
                    ))}

                    {/* Current Year Net Profit Row */}
                    <div className="grid grid-cols-12 text-xs py-1.5 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/20 rounded px-2 text-emerald-700 dark:text-emerald-400 font-medium">
                      <div className="col-span-6 flex items-center gap-2">
                        <span className="font-mono text-[10px] bg-emerald-100 dark:bg-emerald-900/60 px-1 rounded text-emerald-800 dark:text-emerald-300">AUTO</span>
                        <span>Current Year Net Profit</span>
                      </div>
                      <div className="col-span-3 text-right font-mono font-bold">{formatCurrency(data.netProfit)}</div>
                      <div className="col-span-3 text-right font-mono">{calcPercentage(data.netProfit, data.totalLiabilitiesAndEquity)}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Left Footer Total */}
          <div className="grid grid-cols-12 bg-slate-100 dark:bg-slate-900 px-4 py-3 font-bold text-sm text-foreground border-t border-border">
            <div className="col-span-6">Total Liabilities & Equities</div>
            <div className="col-span-3 text-right font-mono text-emerald-700 dark:text-emerald-400">{formatCurrency(data.totalLiabilitiesAndEquity)}</div>
            <div className="col-span-3 text-right font-mono text-xs">100%</div>
          </div>
        </div>

        {/* Right Column: Assets */}
        <div className="bg-white dark:bg-slate-950 rounded-xl border border-border overflow-hidden shadow-sm flex flex-col justify-between h-full">
          <div>
            <div className="grid grid-cols-12 bg-slate-50 dark:bg-slate-900 px-4 py-3 text-xs font-semibold text-muted-foreground border-b border-border">
              <div className="col-span-6">Accounts</div>
              <div className="col-span-3 text-right">Amount</div>
              <div className="col-span-3 text-right">% of Total</div>
            </div>

            {/* Group Header: 1. Asset */}
            <div className="divide-y divide-border/60">
              <div className="px-4 py-3 bg-slate-100/50 dark:bg-slate-900/40 font-bold text-sm text-foreground flex items-center justify-between">
                <button
                  onClick={() => setIsAssetsOpen(!isAssetsOpen)}
                  className="flex items-center gap-2 hover:text-primary transition-colors"
                >
                  {isAssetsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <span>1. Asset</span>
                </button>
                <span className="font-mono text-xs font-bold text-foreground">
                  {formatCurrency(data.totalAssets)}
                </span>
              </div>

              {isAssetsOpen && (
                <div className="px-4 py-2 space-y-1">
                  {data.assets.length === 0 ? (
                    <div className="text-xs text-muted-foreground italic py-3 text-center">No asset accounts recorded.</div>
                  ) : (
                    data.assets.map((acc: ReportAccountBalance) => (
                      <div key={acc.accountId} className="grid grid-cols-12 text-xs py-1.5 hover:bg-slate-50 dark:hover:bg-slate-900/50 rounded px-2">
                        <div className="col-span-6 flex items-center gap-2">
                          <span className="font-mono text-muted-foreground text-[11px]">{acc.code}</span>
                          <span className="text-foreground font-medium">{acc.name}</span>
                        </div>
                        <div className="col-span-3 text-right font-mono font-medium text-foreground">{formatCurrency(acc.netBalance)}</div>
                        <div className="col-span-3 text-right font-mono text-muted-foreground">{calcPercentage(acc.netBalance, data.totalAssets)}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Footer Total */}
          <div className="grid grid-cols-12 bg-slate-100 dark:bg-slate-900 px-4 py-3 font-bold text-sm text-foreground border-t border-border mt-auto">
            <div className="col-span-6">Total Assets</div>
            <div className="col-span-3 text-right font-mono text-emerald-700 dark:text-emerald-400">{formatCurrency(data.totalAssets)}</div>
            <div className="col-span-3 text-right font-mono text-xs">100%</div>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="text-center text-xs text-muted-foreground pt-2 font-medium">
        * Reports are in your business currency INR
      </div>
    </div>
  );
}
